import { useEffect, useRef, useState } from "react";
import { getJson, ago, nf, card } from "../kb/shared";
import { BarList, ColumnChart, ChoroplethMap, SimpleTable, continuousSeries } from "../kb/Charts";
import { TechniqueMatrix } from "./Matrix";

// A dashboard widget is a saved query plus a chart type. It re-queries whenever
// the dashboard's date range changes and on the dashboard's refresh tick, so it
// follows the server's data as the feeds update.

export const RANGES = [
  { k: "7d", l: "7 days" }, { k: "30d", l: "30 days" }, { k: "90d", l: "90 days" }, { k: "1y", l: "1 year" }, { k: "all", l: "All time" },
];
const RANGE_LABEL = Object.fromEntries(RANGES.map(r => [r.k, r.l]));
export const CHART_LABEL = { stat: "Number", bar: "Bar ranking", column: "Over time", map: "World map", table: "Table", matrix: "ATT&CK heatmap" };
export const WIDTHS = [{ k: 4, l: "Small" }, { k: 6, l: "Half" }, { k: 8, l: "Large" }, { k: 12, l: "Full width" }];

// Which chart forms suit a grouping: one number → stat; time → columns; country → map.
export function validCharts(dimKind) {
  if (!dimKind) return ["stat"];
  if (dimKind.startsWith("time")) return ["column", "table"];
  if (dimKind === "country") return ["map", "bar", "table"];
  if (dimKind === "technique") return ["matrix", "bar", "table"];
  return ["bar", "table"];
}

// Dashboard focus as the server expects it: ids only, empty kinds dropped.
export function scopeParam(focus) {
  if (!focus) return null;
  const out = {};
  for (const k of ["threats", "countries", "sectors"]) if (focus[k]?.length) out[k] = focus[k].map(x => x.id);
  return Object.keys(out).length ? out : null;
}

export function queryUrl(w, range, focus) {
  const p = new URLSearchParams({ dataset: w.dataset, measure: w.measure || "count", groupBy: w.groupBy || "", range: range || "all", limit: String(w.limit || 10) });
  const scope = w.ignoreFocus ? null : scopeParam(focus);
  if (scope) p.set("scope", JSON.stringify(scope));
  if (w.chart === "matrix") p.set("all", "1");
  const filters = (w.filters || []).filter(f => f.values?.length).map(({ dim, values, exclude }) => ({ dim, values, exclude: !!exclude }));
  if (filters.length) p.set("filters", JSON.stringify(filters));
  if (!w.groupBy) p.set("compare", "previous");
  return `/api/dash/query?${p}`;
}

export function autoTitle(w, schema) {
  const ds = schema?.find(d => d.key === w.dataset);
  if (!ds) return "Untitled widget";
  const m = ds.measures.find(x => x.key === (w.measure || "count"))?.label || "Count";
  const g = ds.dims.find(x => x.key === w.groupBy)?.label;
  return g ? `${m} by ${g.toLowerCase()}` : m;
}

// Fetch with the previous result held on screen while a refetch is in flight.
export function useQuery(url, refreshKey) {
  const [state, setState] = useState({ data: null, error: "", loading: true });
  const first = useRef(true);
  useEffect(() => {
    if (!url) return;
    let live = true;
    setState(s => ({ ...s, loading: true }));
    getJson(url, { fresh: !first.current })
      .then(data => live && setState({ data, error: "", loading: false }))
      .catch(e => live && setState(s => ({ ...s, error: e.message, loading: false })));
    first.current = false;
    return () => { live = false; };
  }, [url, refreshKey]);
  return state;
}

// The period that's still in progress (today / this month / this year), drawn as partial.
export function currentPeriodKey(unit) {
  const iso = new Date().toISOString();
  return unit === "day" ? iso.slice(0, 10) : unit === "year" ? iso.slice(0, 4) : iso.slice(0, 7);
}

const timeUnit = (kind) => (kind === "time-day" ? "day" : kind === "time-week" ? "week" : kind === "time-year" ? "year" : "month");
function timeLabel(kind) {
  return (k, short) => {
    if (kind === "time-year") return k;
    if (kind === "time-week") {
      const d = new Date(k + "T00:00:00Z").toLocaleDateString(undefined, short ? { month: "short", day: "numeric", timeZone: "UTC" } : { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
      return short ? d : `Week of ${d}`;
    }
    if (kind === "time-day") return new Date(k + "T00:00:00Z").toLocaleDateString(undefined, short ? { month: "short", day: "numeric", timeZone: "UTC" } : { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
    const [y, m] = k.split("-");
    return new Date(Date.UTC(+y, +m - 1, 1)).toLocaleDateString(undefined, short ? { month: "short", year: "2-digit", timeZone: "UTC" } : { month: "long", year: "numeric", timeZone: "UTC" });
  };
}

export function WidgetBody({ data, chart, limit, ui, onDrill }) {
  const { B, fs } = ui;
  if (!data) return null;
  if (!data.total && !data.rows.length) {
    return <div style={{ padding: "26px 0", textAlign: "center", fontSize: fs(12), color: B.textDim }}>No data for this range and these filters.</div>;
  }
  const unit = data.measureLabel.toLowerCase();

  if (chart === "stat" || !data.groupBy) {
    const prev = data.previous;
    let delta = null;
    if (prev !== null && prev !== undefined && data.rangeDays) {
      if (prev === 0) delta = data.total ? "New this period" : null;
      else {
        const pct = Math.round(((data.total - prev) / prev) * 100);
        delta = pct === 0 ? `No change vs previous ${data.rangeDays} days` : `${pct > 0 ? "▲" : "▼"} ${Math.abs(pct)}% vs previous ${data.rangeDays} days (${nf(prev)})`;
      }
    }
    return (
      <div style={{ padding: "6px 0 4px" }}>
        {onDrill
          ? <button onClick={() => onDrill(null, null)} title="Show these records" style={{ fontSize: fs(44), fontWeight: 800, color: B.text, lineHeight: 1.1, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "'Satoshi', sans-serif", textDecoration: "underline", textDecorationColor: B.border, textUnderlineOffset: 6 }}>{nf(data.total)}</button>
          : <div style={{ fontSize: fs(44), fontWeight: 800, color: B.text, lineHeight: 1.1 }}>{nf(data.total)}</div>}
        <div style={{ fontSize: fs(12), color: B.textMuted, marginTop: 4 }}>{data.measureLabel}</div>
        {delta && <div style={{ fontSize: fs(12), color: B.textDim, marginTop: 6 }}>{delta}</div>}
        {!delta && data.noHistory && data.rangeDays && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 6, lineHeight: 1.5 }}>{data.noHistory}</div>}
      </div>
    );
  }

  if (chart === "column" && data.kind?.startsWith("time")) {
    const map = Object.fromEntries(data.rows.map(r => [r.key, r.value]));
    const u = timeUnit(data.kind);
    const points = continuousSeries(map, u, u === "day" ? 120 : undefined);
    return <ColumnChart points={points} unit={unit} formatLabel={timeLabel(data.kind)} partialKey={currentPeriodKey(u)} ui={ui} height={200}
      onSelect={onDrill ? (p) => onDrill({ dim: data.groupBy, key: p.key }, timeLabel(data.kind)(p.key)) : undefined} />;
  }

  if (chart === "matrix" && data.tactics) {
    return <TechniqueMatrix data={data} ui={ui} />;
  }

  if (chart === "map" && data.kind === "country") {
    const values = {}, numeric = {}, names = {};
    for (const r of data.rows) { values[r.key] = r.value; names[r.key] = r.label; if (r.numeric) numeric[r.key] = r.numeric; }
    return <ChoroplethMap mode="count" values={values} numeric={numeric} names={names} unit={unit} tools={false} ui={ui}
      onSelect={onDrill ? (iso2, d) => onDrill({ dim: data.groupBy, key: iso2 }, d.name || iso2) : undefined} />;
  }

  if (chart === "table") {
    const rows = data.rows.map(r => [r.link ? <a href={`#/kb/e/${encodeURIComponent(r.link)}`} style={{ color: B.textMuted, textDecoration: "none" }}>{r.label}</a> : r.label, nf(r.value)]);
    if (data.other) rows.push([`Everything else (${nf(data.groupCount - data.rows.length)})`, nf(data.other)]);
    return <SimpleTable head={[data.groupByLabel || "", data.measureLabel]} rows={rows} ui={ui} />;
  }

  // Bar ranking (default for categories)
  return (
    <BarList
      rows={data.rows.map(r => ({ key: r.key, link: r.link, label: r.label, value: r.value, href: r.link ? `#/kb/e/${encodeURIComponent(r.link)}` : null }))}
      onSelect={onDrill ? (r) => onDrill({ dim: data.groupBy, key: r.key }, r.label, r.link ? { label: r.label, link: r.link } : null) : undefined}
      maxRows={data.kind?.startsWith("time") ? 50 : Math.max(data.rows.length, 1)}
      extraOther={data.other ? { value: data.other, count: data.groupCount - data.rows.length } : null}
      keepOrder unit={data.measureLabel} ui={ui}
    />
  );
}

const KIND_LABEL = { threats: "threats", countries: "countries", sectors: "sectors" };

// One line saying whether the dashboard focus shaped this widget.
export function focusNote(data, w, focus) {
  if (!data?.scope || !scopeParam(focus)) return null;
  if (w.ignoreFocus) return { text: "Ignores the dashboard focus", tone: "dim" };
  const { applied, ignored, inferred } = data.scope;
  if (!applied.length) return { text: `Focus doesn't apply to this data (${ignored.map(k => KIND_LABEL[k]).join(", ")})`, tone: "warn" };
  let text = "Focused";
  if (inferred) text += " · actor links inferred through their software";
  if (ignored.length) text += ` · ${ignored.map(k => KIND_LABEL[k]).join(", ")} not applicable`;
  return { text, tone: inferred ? "warn" : "ok" };
}

export function WidgetCard({ w, range, focus, schema, ui, refreshKey, editing, onEdit, onRemove, onMove, isFirst, isLast, onDrill }) {
  const { B, fs } = ui;
  const url = queryUrl(w, range, focus);
  const title = w.title || autoTitle(w, schema);
  const drill = onDrill ? (match, label, entity) => onDrill({
    title: label ? `${title}: ${label}` : title,
    subtitle: `${RANGE_LABEL[range] || range}${w.filters?.length ? ` · ${w.filters.length} filter${w.filters.length === 1 ? "" : "s"}` : ""}${!w.ignoreFocus && scopeParam(focus) ? " · dashboard focus applied" : ""}`,
    entity,
    params: { dataset: w.dataset, range, filters: w.filters || [], scope: w.ignoreFocus ? null : scopeParam(focus), match },
  }) : null;
  const { data, error, loading } = useQuery(url, refreshKey);
  const ds = schema?.find(d => d.key === w.dataset);
  const btn = { background: "none", border: `1px solid ${B.border}`, borderRadius: 6, color: B.textMuted, cursor: "pointer", fontSize: fs(11), padding: "3px 8px", fontFamily: "'Satoshi', sans-serif" };

  return (
    <div style={{ ...card(ui), padding: "14px 16px", display: "flex", flexDirection: "column", minWidth: 0, height: "100%", boxSizing: "border-box", outline: editing ? `1px dashed ${B.borderLight}` : "none" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: fs(14), fontWeight: 800, color: B.text }}>{w.title || autoTitle(w, schema)}</div>
          <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 2 }}>
            {ds ? ds.label : w.dataset}
            {ds?.hasDate ? ` · ${RANGE_LABEL[range] || range}` : " · all time"}
            {w.filters?.length ? ` · ${w.filters.length} filter${w.filters.length === 1 ? "" : "s"}` : ""}
            {data?.updatedAt ? ` · data ${ago(data.updatedAt)}` : ""}
          </div>
          {(() => {
            const n = focusNote(data, w, focus);
            return n ? <div style={{ fontSize: fs(11), marginTop: 3, color: n.tone === "warn" ? "#d97706" : n.tone === "ok" ? B.textMuted : B.textDim }}>{n.tone === "ok" ? "◉ " : ""}{n.text}</div> : null;
          })()}
        </div>
        {editing && (
          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
            <button onClick={() => onMove(-1)} disabled={isFirst} aria-label="Move earlier" style={{ ...btn, opacity: isFirst ? 0.4 : 1 }}>←</button>
            <button onClick={() => onMove(1)} disabled={isLast} aria-label="Move later" style={{ ...btn, opacity: isLast ? 0.4 : 1 }}>→</button>
            <button onClick={onEdit} style={btn}>Edit</button>
            <button onClick={onRemove} aria-label="Remove widget" style={btn}>✕</button>
          </div>
        )}
      </div>
      <div style={{ flex: 1, opacity: loading && data ? 0.55 : 1, transition: "opacity 150ms" }}>
        {error && <div style={{ fontSize: fs(12), color: "#dc2626" }}>{error}</div>}
        {!data && loading && <div style={{ fontSize: fs(12), color: B.textDim, padding: "20px 0" }}>Loading…</div>}
        {data && <WidgetBody data={data} chart={w.chart} limit={w.limit} ui={ui} onDrill={drill} />}
      </div>
    </div>
  );
}
